import { useEffect, useMemo, useState } from "react";
import {
  useLocalParticipant,
  useSpeakingParticipants,
  useTracks,
  type TrackReferenceOrPlaceholder,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import { tracksWithoutCallMonitor } from "../lib/callMonitor";
import { useVisibleParticipants } from "./useVisibleParticipants";

export interface SpeakerStage {
  mainTrack: TrackReferenceOrPlaceholder | null;
  /** Everyone else's camera tracks, excluding whichever one is main. */
  thumbnails: TrackReferenceOrPlaceholder[];
  isPresenting: boolean;
  /** Solo + camera off + no screen-share — render a "copy meeting link" empty
   *  state instead of a bare avatar tile filling the whole main slot. */
  isSoloEmpty: boolean;
  speakingSids: Set<string>;
}

/**
 * Shared "one main tile + everyone else" selection logic for the design's
 * `speaker` (horizontal strip) and `sidebar` (vertical strip) layouts — they
 * differ only in strip orientation, not in which tile is main. Screen-share
 * always wins the main slot; failing that, a STICKY pin on the loudest
 * speaker. LiveKit's active-speaker list flickers — it empties between
 * utterances and reorders on small audio-level changes — so we only update
 * the pin when there's a current speaker that actually has a tile; during
 * silence the last speaker stays pinned.
 */
export function useSpeakerStage(
  options: { excludeLocalFromMain?: boolean } = {},
): SpeakerStage {
  // The floating PiP window should show other people, not the viewer: with
  // this on, the local participant never takes the sticky pin (self is main
  // only when nobody else has a camera tile, via the fallback below).
  const { excludeLocalFromMain = false } = options;
  const allCameraTracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
    { onlySubscribed: false },
  );
  // The staging call monitor has no camera, but a placeholder would still
  // give it a tile.
  const cameraTracks = useMemo(() => tracksWithoutCallMonitor(allCameraTracks), [allCameraTracks]);
  const screenTracks = useTracks(
    [{ source: Track.Source.ScreenShare, withPlaceholder: false }],
    { onlySubscribed: false },
  );
  const participants = useVisibleParticipants();
  const speakingParticipants = useSpeakingParticipants();
  const { isCameraEnabled } = useLocalParticipant();

  const cameraSids = useMemo(
    () => new Set(cameraTracks.map((t) => t.participant.sid)),
    [cameraTracks],
  );
  const [pinnedSid, setPinnedSid] = useState<string | null>(null);
  useEffect(() => {
    const speaker = speakingParticipants.find(
      (p) => cameraSids.has(p.sid) && !(excludeLocalFromMain && p.isLocal),
    );
    if (speaker) setPinnedSid(speaker.sid);
  }, [speakingParticipants, cameraSids, excludeLocalFromMain]);

  const mainTrack = useMemo<TrackReferenceOrPlaceholder | null>(() => {
    if (screenTracks.length > 0) return screenTracks[0];
    if (cameraTracks.length === 0) return null;
    if (pinnedSid) {
      const pinned = cameraTracks.find((c) => c.participant.sid === pinnedSid);
      if (pinned) return pinned;
    }
    // Before anyone has spoken (or the pinned speaker left): first remote,
    // falling back to self.
    const firstRemote = cameraTracks.find((t) => !t.participant.isLocal);
    return firstRemote ?? cameraTracks[0];
  }, [screenTracks, cameraTracks, pinnedSid]);

  const thumbnails = useMemo(
    () =>
      cameraTracks.filter(
        (t) =>
          !(
            mainTrack &&
            t.participant.sid === mainTrack.participant.sid &&
            t.source === mainTrack.source
          ),
      ),
    [cameraTracks, mainTrack],
  );

  const speakingSids = useMemo(
    () => new Set(speakingParticipants.map((p) => p.sid)),
    [speakingParticipants],
  );

  const isPresenting = screenTracks.length > 0;
  const hasRemotes = participants.some((p) => !p.isLocal);
  const isSoloEmpty = !hasRemotes && !isCameraEnabled && !isPresenting;

  return { mainTrack, thumbnails, isPresenting, isSoloEmpty, speakingSids };
}
